/**
 * Result shape for `parseJson`. `ok` mirrors the standard Result pattern —
 * callers can branch on `result.ok` and TypeScript narrows `value` and
 * `error` accordingly.
 */
export interface ParseJsonResult<T> {
  ok: boolean;
  value: T | null;
  error: string | null;
}

/**
 * Wraps `JSON.parse` so callers never need a try/catch.
 *
 * On success: `{ ok: true,  value: parsed, error: null }`
 * On failure: `{ ok: false, value: null,   error: '...' }`
 *
 * The error string is derived from the thrown `Error.message` when one is
 * available; otherwise the generic 'Invalid JSON' message is returned.
 *
 * Generic `T` lets callers assert a shape — the helper does no runtime
 * validation, so use a schema validator (e.g. Zod) when the source is
 * untrusted.
 */
export const parseJson = <T>(raw: string): ParseJsonResult<T> => {
  try {
    return { ok: true, value: JSON.parse(raw) as T, error: null };
  } catch (err) {
    return { ok: false, value: null, error: err instanceof Error ? err.message : 'Invalid JSON' };
  }
};
